<script lang="ts">
import { defineComponent } from 'vue'
import { format } from 'date-fns'
import NrIconButton from '@nextrequestco/components/src/components/NrIconButton'
import NrIcon from '@nextrequestco/components/src/components/NrIcon'
import NrModal from '@nextrequestco/components/src/components/NrModal'
import NrTooltip from '@nextrequestco/components/src/components/NrTooltip'
import NrDatePicker from '@nextrequestco/components/src/components/NrDatePicker'
import { mapActions } from 'vuex'

export default defineComponent({
  name: 'request-fulfillment-date',
  components: { NrIconButton, NrIcon, NrTooltip, NrModal, NrDatePicker },
  props: {
    prettyId: { type: String },
    date: { type: String }
  },
  data() {
    return {
      showModal: false,
      updating: false,
      newDate: null
    }
  },
  computed: {
    computedDate(): string {
      if (Date.parse(this.date)) {
        return format(new Date(this.date), 'MM/dd/yyyy')
      }
      return ''
    },
    unselectableDates(): string[] {
      if (this.computedDate) {
        return [this.computedDate]
      } else {
        return []
      }
    }
  },
  methods: {
    ...mapActions('request_show', ['updateRequest', 'fetchTimeline']),

    handleDateInput(date: Date) {
      this.newDate = date
    },

    async handleSaveData() {
      if (!this.newDate) return

      this.updating = true
      try {
        await this.updateRequest({
          request_id: this.prettyId,
          anticipated_fulfilled_at: format(new Date(this.newDate), 'MM/dd/yyyy')
        })
        this.fetchTimeline({ pretty_id: this.prettyId })
        this.$toasted.success(
          'Successfully updated anticipated fulfillment date.'
        )
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        this.$toasted.error(
          'There has been an error saving the anticipated fulfillment date. Please refresh the page and try again.'
        )
      }

      this.updating = false
      this.toggleModal(false)
    },

    toggleModal(state?: boolean) {
      this.showModal = state ?? !this.showModal
    }
  }
})
</script>

<template>
  <div class="panel-section p-bottom-8 request-fulfillment-date">
    <div
      class="panel-item is-flex is-align-items-center is-justify-content-space-between"
      :class="{ 'panel-item--editable': $can('publish', 'Request') }"
    >
      <p class="has-text-weight-semibold">Anticipated fulfillment</p>
      <NrTooltip
        label="Edit anticipated fulfillment date"
        v-if="$can('publish', 'Request')"
      >
        <NrIconButton
          variant="minimal-neutral"
          :icon="{ name: 'pencil-alt', type: 'regular' }"
          aria-label="Edit anticipated fulfillment date"
          @click="toggleModal(true)"
        />
      </NrTooltip>
    </div>
    <div class="m-left-8">
      <p>{{ date }}</p>
    </div>

    <NrModal
      title="Change anticipated fulfillment date"
      :show="showModal"
      :hasSecondaryButton="true"
      :disabeButtons="updating"
      secondaryButtonText="Cancel"
      primaryButtonText="Save date"
      @on-close-click="toggleModal(false)"
      @on-secondary-click="toggleModal(false)"
      @on-primary-click="handleSaveData"
    >
      <form @submit.prevent.stop>
        <fieldset>
          <NrDatePicker
            @input="handleDateInput"
            placeholder="Type or select a date (mm/dd/yyyy)"
            :modelValue="computedDate"
            :unselectable-dates="unselectableDates"
          >
            <template v-slot:label>New anticipated fulfillment date</template>
            <template v-slot:trigger><NrIcon name="calendar-alt" /></template>
          </NrDatePicker>
          <p class="help">
            Changing this date may have legal consquences. Please refer to
            appropriate laws.
          </p>
        </fieldset>
      </form>
    </NrModal>
  </div>
</template>
